// @givmo/app-charity — §321 grants/donations decode + display helpers (CPA-WEB5).
//
// ★ NUMBERS-ARE-THE-LAW. Every money figure on the §321 statement is rendered EXACTLY as the backend
// returned it. The backend (BE7) already tied each amount to the ledger to the penny (gross = net +
// fee, wallet legs included), so this module NEVER recomputes, re-sums, re-derives, or re-rounds a
// money value. Money fields cross the wire as DECIMAL STRINGS (never numbers); `displayMoney` only
// *formats* the string for presentation (currency marker, thousands separators + 2 decimals) via the
// shared string-only `formatMoneyDisplay`, and falls back to the raw wire string if it isn't a plain
// decimal (tolerant-reader). There is no `parseFloat`, no arithmetic, no `Number(...)` on any amount.
//
// STATUS is likewise rendered verbatim: `status_label` is the backend's vocab-clean one-line copy, and
// `status` is the vocab-clean token (`preparing`/`sent`/`returned`) — we render the label as-is and only
// map the token to a BADGE TONE (a purely visual concern; the text is never re-mapped). An unknown
// future token degrades to a neutral tone but the label still shows.

import type { BadgeProps } from '@givmo/design-system';
import { formatMoneyDisplay } from '../../lib/format-money';

/** The Badge's tone union (the design-system doesn't re-export the tone type directly). */
type BadgeTone = NonNullable<BadgeProps['tone']>;

/**
 * Format a wire money STRING for display: dollar-marked, grouped, 2 decimals.
 * MONEY LAW: this only reformats the decimal string — never a float round-trip. If the value isn't a
 * plain decimal (or is null/undefined), returns the raw string verbatim (or an em dash) so nothing is
 * ever silently dropped or altered. NEVER call this on a value you intend to sum — there is no summing.
 */
export function displayMoney(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  return formatMoneyDisplay(value);
}

/** Format an ISO date/timestamp for display; tolerant of null/invalid values → em dash. */
export function displayDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Format a plain count (donation_count etc.) — an integer, shown verbatim; tolerant of null. */
export function displayCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(value);
}

/**
 * Badge tone for a vocab-clean grant `status` token. This is PURELY VISUAL — the human-facing text is
 * always the backend's `status_label`, never re-derived from this. An unknown future token → neutral.
 */
export function grantStatusTone(status: string | null | undefined): BadgeTone {
  switch ((status ?? '').toLowerCase()) {
    case 'sent':
      return 'success';
    case 'returned':
      return 'warning';
    case 'preparing':
      return 'neutral';
    default:
      return 'neutral';
  }
}

// ── FCA-WEB3: the creator-message framing ────────────────────────────────────────────────────────────
//
// `message_framing` is an additive OPTIONAL response field on the §321 fundraiser detail that the
// committed OpenAPI snapshot does not carry yet (the snapshot tracks the backend's LANDED
// `development` spec; this field ships on an unmerged backend branch). Per agent_docs/api-client.md →
// "Additive RESPONSE fields the snapshot lacks", it is read off the body as `unknown` in a hand decode
// rather than by regenerating from an unlanded spec. DELETE this helper and read the generated field
// once a snapshot refresh absorbs it.
//
// ★ The framing is the GC-authored attribution / non-endorsement / non-binding line that FCA-LEG2
// flip-condition 2 requires beside EVERY rendered creator-message. It is REGISTRY-SERVED: the string
// itself lives in a versioned, President-signed disclaimer row, never in this repo. So this function
// only decides whether the server sent one — it never composes, defaults, or substitutes copy.

/**
 * The framing string the server sent beside a creator-message, or `null`.
 *
 * FAIL-CLOSED by design, matching the server: anything that is not a non-empty string — absent, null,
 * a number, an object — reads as "no framing", and the caller must then render NO message panel at
 * all. Reading an unrecognized shape as "close enough" would be guessing in the one direction that
 * puts a supporter's words in front of a charity without saying whose they are.
 *
 * Returns the string VERBATIM (trimmed of surrounding whitespace only) — the bytes are signed copy,
 * so they are never re-wrapped, truncated, or re-punctuated for presentation.
 */
export function readMessageFraming(detail: unknown): string | null {
  if (typeof detail !== 'object' || detail === null) return null;
  const value = (detail as { message_framing?: unknown }).message_framing;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
